/** Shared helpers for order form departure date/time fields. */

const DEPARTURE_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DEPARTURE_TIME_RE = /^(\d{2}):(\d{2})$/;

export const isCompleteDepartureDate = (date: Date): boolean => {
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  const year = date.getFullYear();
  // Reject mid-typing years (e.g. year 20 while entering 2026 via DatePicker sections).
  return year >= 2000 && year <= 2100;
};

export const parseDepartureDateString = (dateString: string): Date | null => {
  if (!dateString.trim()) {
    return null;
  }

  const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const year = Number.parseInt(dateMatch[1], 10);
    const month = Number.parseInt(dateMatch[2], 10) - 1;
    const day = Number.parseInt(dateMatch[3], 10);
    const parsed = new Date(year, month, day);
    return isCompleteDepartureDate(parsed) ? parsed : null;
  }

  return null;
};

export const parseDepartureTimeString = (timeString: string): Date | null => {
  if (!timeString.trim()) {
    return null;
  }

  const timeMatch = timeString.match(/^(\d{2}):(\d{2})/);
  if (!timeMatch) {
    return null;
  }

  const hours = Number.parseInt(timeMatch[1], 10);
  const minutes = Number.parseInt(timeMatch[2], 10);
  if (hours > 23 || minutes > 59) {
    return null;
  }

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

export const formatDepartureDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatDepartureTimeString = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const isValidStoredDepartureDate = (dateString: string): boolean => {
  const match = dateString.trim().match(DEPARTURE_DATE_RE);
  if (!match) {
    return false;
  }
  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);
  if (year < 2000 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const testDate = new Date(year, month - 1, day);
  return (
    testDate.getFullYear() === year &&
    testDate.getMonth() === month - 1 &&
    testDate.getDate() === day
  );
};

export const isValidStoredDepartureTime = (timeString: string): boolean => {
  const match = timeString.trim().match(DEPARTURE_TIME_RE);
  if (!match) {
    return false;
  }
  const hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  return hours <= 23 && minutes <= 59;
};
